<template>
  <div class="recollection-management">
    <div class="recollection-management__cards">
      <KpiCard
        title="Recoletas por O.S."
        :value="recollectionsByOrder ? formatNumber(recollectionsByOrder.total) : '--'"
        :complement="
          recollectionsByOrder
            ? `de ${formatNumber(recollectionsByOrder.totalExams)}`
            : ''
        "
      />
      <KpiCard
        title="Recoletas por exames"
        :value="recollectionsByExam ? formatNumber(recollectionsByExam.total) : '--'"
        :complement="
          recollectionsByExam ? `de ${formatNumber(recollectionsByExam.totalExams)}` : ''
        "
      />
      <KpiCard
        title="Taxa de Recoleta"
        :value="collectionRate !== null ? `${formatNumber(collectionRate, 2)}%` : '--'"
        description="do total de exames"
        :positive="isBelowAlvaroTarget"
        :negative="!isBelowAlvaroTarget"
      />
      <KpiCard
        title="Meta Álvaro"
        info="Representa a meta do Álvaro Apoio para a taxa de recoleta. Se a sua “Taxa de Recoleta” estiver maior que a Meta Álvaro, planos de ação e treinamentos são necessários."
        :value="alvaroTarget !== null ? `${formatNumber(alvaroTarget, 2)}%` : '--'"
        description="do total de exames"
        :positive="true"
      />
    </div>

    <div class="recollection-management__row">
      <div class="recollection-management__col">
        <ReasonsChart
          title="Top 5 Motivos de Recoletas"
          :data="reasonsData"
          :loading="loading"
          @slice-click="onChartClick"
        />
      </div>
      <div class="recollection-management__col">
        <LineChart
          title="Evolução da Taxa de Recoleta (%)"
          :categories="months"
          :values="collectionRateValues"
          series-name="Taxa de Recoleta"
          unit="%"
          :target="alvaroTarget"
          :target-label="
            alvaroTarget !== null ? `Meta Álvaro (${formatNumber(alvaroTarget, 2)}%)` : ''
          "
          :loading="loading"
          @point-click="onChartClick"
        />
      </div>
    </div>

    <div class="recollection-management__row">
      <div class="recollection-management__col">
        <HorizontalBarChart
          title="Recoletas por Exames"
          :data="examsData"
          :loading="loading"
          @bar-click="onChartClick"
        />
      </div>
      <div class="recollection-management__col">
        <HorizontalBarChart
          title="Recoletas por NTO"
          :data="ntoData"
          :loading="loading"
          @bar-click="onChartClick"
        />
      </div>
    </div>

    <ReminderCard
      titulo="Detalhes das recoletas"
      descricao="Para acessar mais detalhes sobre cada recoleta, já disponibilizamos um relatório de Gestão de Recoletas."
      texto-botao="Acessar Relatórios"
      @click-botao="onAccessReports"
    />
  </div>
</template>

<script>
import '../charts/echartsSetup'
import KpiCard from '../charts/KpiCard.vue'
import ReasonsChart from '../charts/ReasonsChart.vue'
import LineChart from '../charts/LineChart.vue'
import HorizontalBarChart from '../charts/HorizontalBarChart.vue'
import ReminderCard from '../../common/ReminderCard.vue'
import biRecollections from '../../../api/biRecollections'

export default {
  name: 'RecollectionManagement',
  components: {
    KpiCard,
    ReasonsChart,
    LineChart,
    HorizontalBarChart,
    ReminderCard,
  },
  props: {
    period: { type: String, default: null },
  },
  data() {
    return {
      loading: false,
      recollectionsByOrder: null,
      recollectionsByExam: null,
      collectionRate: null,
      alvaroTarget: null,
      months: [],
      collectionRateValues: [],
      reasonsData: [],
      examsData: [],
      ntoData: [],
    }
  },
  computed: {
    isBelowAlvaroTarget() {
      return (
        this.collectionRate !== null &&
        this.alvaroTarget !== null &&
        this.collectionRate < this.alvaroTarget
      )
    },
    currentPeriod() {
      return new Date().toISOString().slice(0, 7)
    },
  },
  watch: {
    period(val) {
      if (val) this.fetchAll(val)
    },
  },
  created() {
    this.fetchAll(this.period || this.currentPeriod)
  },
  methods: {
    formatNumber(value, decimals = 0) {
      if (value === null || value === undefined || value === '') return '--'
      const numericValue = Number(value)
      if (!Number.isFinite(numericValue)) return '--'
      return numericValue.toLocaleString('pt-BR', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    },
    fetchAll(period) {
      this.fetchRecollections(period)
    },
    async fetchPeriods(period) {
      try {
        await biRecollections.getPeriods(period)
      } catch (e) {
        console.error('[BI] Erro ao buscar períodos:', e)
      }
    },
    async fetchRecollections(period) {
      this.loading = true
      this.months = []
      this.collectionRateValues = []
      this.reasonsData = []
      this.examsData = []
      this.ntoData = []
      try {
        const { data } = await biRecollections.getRecollections(period)
        this.mapData(data)
      } catch (e) {
        console.error('[BI] Erro ao buscar recoletas:', e)
      } finally {
        this.loading = false
      }
    },
    mapData(data) {
      if (!data) return
      const s = data.summary || {}
      this.recollectionsByOrder = s.ordersWithRecollection
        ? {
            total: s.ordersWithRecollection.value,
            totalExams: s.ordersWithRecollection.total,
          }
        : null
      this.recollectionsByExam = s.examsWithRecollection
        ? {
            total: s.examsWithRecollection.value,
            totalExams: s.examsWithRecollection.total,
          }
        : null
      this.collectionRate = s.recollectionRate?.value ?? null
      this.alvaroTarget = s.recollectionTarget?.value ?? null

      const evolution = data.charts?.recollectionRateEvolution?.items ?? []
      this.months = evolution.map(t => this.formatMonth(t.period))
      this.collectionRateValues = evolution.map(t => t.rate)
      if (evolution.length) {
        this.alvaroTarget = this.alvaroTarget ?? evolution[0].target
      }

      const reasons = data.charts?.recollectionReasons?.reasons ?? []
      this.reasonsData = reasons.map(r => ({
        name: r.description,
        value: r.quantity,
        percent: r.percentage,
      }))

      const exams = data.charts?.recollectionsByExam?.items ?? []
      this.examsData = exams.map(e => ({
        name: e.description,
        value: e.quantity,
        label: this.formatNumber(e.quantity),
        percent: e.percentage,
      }))

      const ntos = data.charts?.recollectionsByNto?.items ?? []
      this.ntoData = ntos.map(n => ({
        name: n.description,
        value: n.quantity,
        label: this.formatNumber(n.quantity),
        percent: n.percentage,
      }))

      if (data.dataStatus?.updatedAt) {
        this.$emit('updated-at', data.dataStatus.updatedAt)
      }
    },
    formatMonth(period) {
      const months = [
        'Jan',
        'Fev',
        'Mar',
        'Abr',
        'Mai',
        'Jun',
        'Jul',
        'Ago',
        'Set',
        'Out',
        'Nov',
        'Dez',
      ]
      const [year, month] = period.split('-')
      return `${months[parseInt(month, 10) - 1]}\n${year.slice(2)}`
    },
    onChartClick(payload) {
      this.$emit('chart-click', payload)
    },
    onAccessReports() {
      this.$emit('access-reports')
      this.$router.push({ path: '/gerar-relatorios/', query: { tipo: 'recoletas' } })
    },
    mapCardsData() {},
    mapChartsData() {},
  },
}
</script>

<style scoped lang="scss">
.recollection-management {
  &__cards {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    align-items: stretch;
  }

  &__row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
  }

  &__col {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
}
</style>
